import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

const LOCK_STATE_KEY = 'app_lock_state';
const LOCK_TIMESTAMP_KEY = 'app_lock_timestamp';

export function useInactivityLock(userId: string | null) {
  // Check if we were locked before (persists across page refresh)
  const getInitialLockState = () => {
    if (!userId) return false;
    const storedLockState = sessionStorage.getItem(LOCK_STATE_KEY);
    const storedUserId = sessionStorage.getItem('locked_user_id');
    // Only restore lock state if it's for the same user
    return storedLockState === 'true' && storedUserId === userId;
  };

  const [isLocked, setIsLocked] = useState(getInitialLockState);
  const [hasPin, setHasPin] = useState(false);
  const [timeoutSeconds, setTimeoutSeconds] = useState(120); // Default 2 minutes
  const [remainingTime, setRemainingTime] = useState(120 * 1000);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Check if user has a PIN set and get custom timeout
  useEffect(() => {
    if (!userId) {
      setHasPin(false);
      return;
    }

    const checkSettings = async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('pin, lock_timeout')
        .eq('id', userId)
        .maybeSingle();

      if (!error && data) {
        setHasPin(!!data.pin);
        const customTimeout = data.lock_timeout || 120;
        setTimeoutSeconds(customTimeout);
        setRemainingTime(customTimeout * 1000);
      } else {
        setHasPin(false);
      }
    };

    checkSettings();
  }, [userId]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    const timeoutMs = timeoutSeconds * 1000;
    setRemainingTime(timeoutMs);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (hasPin && userId && !isLocked) {
      timeoutRef.current = setTimeout(() => {
        // Persist lock state across page refresh
        sessionStorage.setItem(LOCK_STATE_KEY, 'true');
        sessionStorage.setItem(LOCK_TIMESTAMP_KEY, Date.now().toString());
        sessionStorage.setItem('locked_user_id', userId);
        setIsLocked(true);
      }, timeoutMs);
    }
  }, [hasPin, userId, isLocked, timeoutSeconds]);

  const unlock = useCallback(() => {
    // Clear lock state from sessionStorage when unlocking
    sessionStorage.removeItem(LOCK_STATE_KEY);
    sessionStorage.removeItem(LOCK_TIMESTAMP_KEY);
    sessionStorage.removeItem('locked_user_id');
    setIsLocked(false);
    resetTimer();
  }, [resetTimer]);

  const lockNow = useCallback(() => {
    if (hasPin && userId) {
      // Persist lock state across page refresh
      sessionStorage.setItem(LOCK_STATE_KEY, 'true');
      sessionStorage.setItem(LOCK_TIMESTAMP_KEY, Date.now().toString());
      sessionStorage.setItem('locked_user_id', userId);
      setIsLocked(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  }, [hasPin, userId]);

  // Countdown timer update
  useEffect(() => {
    if (!hasPin || !userId || isLocked) {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      return;
    }

    countdownRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, (timeoutSeconds * 1000) - elapsed);
      setRemainingTime(remaining);
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [hasPin, userId, isLocked]);

  // Set up activity listeners
  useEffect(() => {
    if (!hasPin || !userId) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      if (!isLocked) {
        resetTimer();
      }
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Start the initial timer
    resetTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [hasPin, userId, isLocked, resetTimer]);

  // Re-check settings when it might have been updated
  const refreshPinStatus = useCallback(async () => {
    if (!userId) return;

    const { data, error } = await supabase
      .from('profiles')
      .select('pin, lock_timeout')
      .eq('id', userId)
      .maybeSingle();

    if (!error && data) {
      setHasPin(!!data.pin);
      const customTimeout = data.lock_timeout || 120;
      setTimeoutSeconds(customTimeout);
      setRemainingTime(customTimeout * 1000);

      // If PIN was removed, clear lock state
      if (!data.pin) {
        sessionStorage.removeItem(LOCK_STATE_KEY);
        sessionStorage.removeItem(LOCK_TIMESTAMP_KEY);
        sessionStorage.removeItem('locked_user_id');
        setIsLocked(false);
      } else {
        // Start timer after PIN is set
        resetTimer();
      }
    } else {
      setHasPin(false);
      setIsLocked(false);
      sessionStorage.removeItem(LOCK_STATE_KEY);
      sessionStorage.removeItem(LOCK_TIMESTAMP_KEY);
      sessionStorage.removeItem('locked_user_id');
    }
  }, [userId, resetTimer]);

  // Clean up lock state when user changes or component unmounts
  useEffect(() => {
    return () => {
      // Only clear if we're logging out (userId becomes null)
      if (!userId) {
        sessionStorage.removeItem(LOCK_STATE_KEY);
        sessionStorage.removeItem(LOCK_TIMESTAMP_KEY);
        sessionStorage.removeItem('locked_user_id');
      }
    };
  }, [userId]);

  return {
    isLocked,
    hasPin,
    remainingTime,
    unlock,
    lockNow,
    refreshPinStatus,
  };
}